import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  CheckCircle2,
  Phone,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import { SERVICE_PAGES } from "@/data/servicePages";

const SITE_URL = "https://deepcleanrr.com";

const ServicesIndexPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Deep Clean Restroom Service Locations",
    url: `${SITE_URL}/services`,
    itemListElement: SERVICE_PAGES.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.title,
      url: `${SITE_URL}${p.path}`,
    })),
  };

  return (
    <main data-testid="services-index-page" className="bg-white">
      <SEO
        title="Service Locations | Deep Clean Restroom Services"
        description="Commercial restroom cleaning, deep-foam sanitation, and odor control across Wilmington, Wrightsville Beach, and the wider Cape Fear region. Find your location."
        canonical={`${SITE_URL}/services`}
        image={SERVICE_PAGES[0]?.coverImage}
        type="website"
        jsonLd={jsonLd}
        keywords={[
          "commercial restroom cleaning Wilmington",
          "Wrightsville Beach restroom sanitation",
          "deep foam cleaning North Carolina",
          "New Hanover County janitorial",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/ox8gog9f_Deep%20Clean%20Restroom%20Tampa.png')",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.86) 0%, rgba(5,9,19,0.60) 30%, rgba(5,9,19,0.45) 100%), linear-gradient(90deg, rgba(5,9,19,0.82) 0%, rgba(5,9,19,0.45) 55%, rgba(5,9,19,0.10) 100%), radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.22), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-20 pt-16 md:px-12 md:pb-28 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <MapPin size={14} strokeWidth={2} className="text-cyan-300" />
            Service Locations
          </p>
          <h1
            data-testid="services-index-heading"
            className="max-w-5xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl"
          >
            Commercial restroom cleaning
            <br />
            <span className="text-cyan-300">across the Cape Fear region.</span>
          </h1>
          <p className="mt-6 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
            Pick the service page closest to your facility. Each one details
            our deep-foam sanitation, pressure washing, and disinfecting
            programs — built for high-traffic commercial restrooms.
          </p>
        </div>
      </section>

      {/* SERVICE CARDS */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {SERVICE_PAGES.map((p, i) => (
              <article
                key={p.slug}
                data-testid={`service-card-${p.slug}`}
                className="group flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white transition-colors hover:border-blue-600"
              >
                <Link to={p.path} className="block">
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={p.coverImage}
                      alt={p.coverAlt || p.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-7 md:p-9">
                  <p className="inline-flex items-center gap-1.5 font-body text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
                    <MapPin size={12} strokeWidth={2.4} />
                    {p.city} · {p.county}
                  </p>
                  <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
                    <Link
                      to={p.path}
                      data-testid={`service-card-link-${p.slug}`}
                      className="transition-colors hover:text-blue-600"
                    >
                      {p.title}
                    </Link>
                  </h2>
                  <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 md:text-base">
                    {p.description}
                  </p>

                  <ul className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {p.highlights.map((h, j) => (
                      <li
                        key={h}
                        data-testid={`service-highlight-${p.slug}-${j}`}
                        className="flex items-start gap-2 font-body text-sm font-semibold text-slate-700"
                      >
                        <CheckCircle2
                          size={14}
                          strokeWidth={2.4}
                          className="mt-0.5 shrink-0 text-blue-600"
                        />
                        {h}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={p.path}
                    data-testid={`service-cta-${p.slug}`}
                    className="group/btn mt-7 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-blue-600 font-body text-sm font-bold tracking-wide text-white transition-colors hover:bg-blue-700"
                  >
                    View {p.city.split(",")[0]} service page
                    <ArrowRight
                      size={14}
                      strokeWidth={2.4}
                      className="transition-transform group-hover/btn:translate-x-1"
                    />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-16 text-white md:py-20">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Not seeing your area?
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
              We service businesses across the entire Cape Fear region.
            </h2>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="services-index-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Request a Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="services-index-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ServicesIndexPage;
